import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { LogoutOutlined, OpenInNewOutlined } from "@mui/icons-material";
import BarTabs from "./BarTabs";
import Logo1 from "../logo1_ai copy436_alpha.png";
import { tokens } from "../theme/theme";
import { CHRISTMAS, CHROME, HALLOWEEN } from "../public/halloween/season";
import HeaderGarland from "../public/halloween/HeaderGarland";
import { WitchHat } from "../public/halloween/sprites";
import Sprite from "../public/christmas/Sprite";
import { ICONS } from "../public/christmas/atlas";
import { IcicleEdge } from "../public/christmas/Edges";
import {
  useMaintenanceFlag,
  MaintenanceButton,
  MaintenanceBanner,
} from "./MaintenanceSwitch";

const ResponsiveAppBar = ({
  listObjects,
  listClients,
  listArchives,
  openWatermark,
  admin,
  ddbClient,
}) => {
  // Only admins may switch the public site off, so only they pay for the read.
  const maintenance = useMaintenanceFlag(ddbClient, admin);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="transparent"
      sx={{
        backgroundColor: CHROME ? CHROME.panelBar : "rgba(255,255,255,0.92)",
        backdropFilter: "saturate(180%) blur(14px)",
        borderBottom: "1px solid " + (CHROME ? CHROME.border : tokens.line),
      }}
    >
      {/* Inside the sticky bar on purpose — a banner you can scroll away from
          is a banner you stop noticing. */}
      {admin && <MaintenanceBanner state={maintenance} />}

      <Container maxWidth={false} sx={{ px: { xs: 1.5, md: 3 } }}>
        <Toolbar disableGutters sx={{ minHeight: { xs: 58, md: 66 }, gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box sx={{ position: "relative", display: "flex" }}>
              <Box
                component="img"
                src={Logo1}
                alt="LeoCity"
                sx={{
                  height: 38,
                  // White on the night bar, as the public footer does it.
                  filter: CHROME ? "brightness(0) invert(1)" : "none",
                }}
              />
              {HALLOWEEN && (
                // Perched on the tallest of the logo's towers.
                <WitchHat
                  style={{
                    position: "absolute",
                    top: -15,
                    left: "calc(60% - 16px)",
                    width: 32,
                    transform: "rotate(12deg)",
                    pointerEvents: "none",
                  }}
                />
              )}
              {CHRISTMAS && (
                <Sprite
                  atlas={ICONS}
                  name="santaHat"
                  scale={0.3}
                  style={{
                    position: "absolute",
                    // Perched on the tallest of the logo's towers.
                    top: -15,
                    left: "calc(60% - 16px)",
                    transform: "rotate(14deg)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{ lineHeight: 1.15, color: CHROME ? "#fff" : undefined }}
              >
                LeoCity
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: CHROME ? "rgba(255,255,255,0.55)" : tokens.ink400 }}
              >
                Панель рієлтора
              </Typography>
            </Box>
          </Stack>

          {/* minWidth:0 lets this flex item shrink: without it the scrollable Tabs
              keep their full natural width, the toolbar grows wider than a
              phone screen, and everything to the right — including the
              maintenance button — is pushed off-screen. */}
          <Box sx={{ ml: { xs: 0, md: 2 }, minWidth: 0, overflow: "hidden" }}>
            <BarTabs
              listObjects={listObjects}
              listClients={listClients}
              listArchives={listArchives}
              openWatermark={openWatermark}
              admin={admin}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            size="small"
            label={admin ? "Адміністратор" : "Рієлтор"}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              backgroundColor: CHROME
                ? admin
                  ? CHROME.chipBg
                  : "rgba(255,255,255,0.1)"
                : admin
                ? tokens.sand100
                : tokens.blue50,
              color: CHROME
                ? admin
                  ? CHROME.accentText
                  : "#fff"
                : admin
                ? tokens.sand700
                : tokens.blue700,
            }}
          />

          {admin && <MaintenanceButton state={maintenance} />}

          <Tooltip title="Відкрити публічний сайт у новій вкладці">
            <Button
              size="small"
              variant="text"
              startIcon={<OpenInNewOutlined sx={{ fontSize: 17 }} />}
              onClick={() => window.open("/", "_blank")}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                color: CHROME ? "rgba(255,255,255,0.72)" : tokens.ink500,
              }}
            >
              Сайт
            </Button>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            startIcon={<LogoutOutlined sx={{ fontSize: 17 }} />}
            onClick={() => window.open("/", "_self")}
            sx={
              CHROME
                ? {
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.28)",
                    "&:hover": {
                      borderColor: CHROME.accent,
                      backgroundColor: CHROME.accentHover,
                    },
                  }
                : undefined
            }
          >
            Вийти
          </Button>
        </Toolbar>
      </Container>
      {HALLOWEEN && <HeaderGarland compact />}
      {CHRISTMAS && <IcicleEdge compact />}
    </AppBar>
  );
};

export default ResponsiveAppBar;
